/**
 * F3 BrainRepoWriter over the GitHub git-data API (DESIGN §2.4, §4.4).
 *
 * commitBatch drives the low-level git-data dance — blobs -> tree -> commit ->
 * update-ref — entirely over an injected `fetch`, so the unit tests never hit
 * GitHub. The load-bearing CAS is the commit's SINGLE parent: it IS the
 * `expectedOldSha`, and `update-ref` is `force:false`, so GitHub rejects a stale
 * base as a non-fast-forward (422). `mintToken` is bound to
 * `mintInstallationToken().token` at the edge — never invoked in tests.
 */
import type { BrainRepoWriter, FileChange, MemoryFile } from "./types";

export interface GitDataWriterArgs {
  repository: string;                 // "owner/name"
  branch: string;                     // "main"
  fetchImpl?: typeof fetch;           // injected in tests
  mintToken: () => Promise<string>;   // bound to mintInstallationToken().token at the edge (§2.4)
  /**
   * Overrides the B1 write-path allowlist below (WRITER_ALLOWED_PREFIX) for THIS writer
   * instance — additive: absent ⇒ the existing memory/inbox/quarantine/artifacts default is
   * unchanged, so every existing caller (dreamer, exam-consolidate, CLI) needs no changes.
   * Added for E2-F5 Task 6's feed-repo snapshot committer, which writes reports/<brain>/...
   * to a DIFFERENT repo than the brain repo and needs its own narrow surface (`/^reports\//`).
   * The path-traversal guard (no ".." segment) always applies regardless of prefix.
   */
  allowedPathPrefix?: RegExp;
}

const API = "https://api.github.com";

/**
 * Defense-in-depth write-path allowlist (B1 SECURITY BLOCKER). commitBatch is the
 * FINAL choke point before update-ref, shared by BOTH the delta batch (memory/,
 * inbox/, quarantine/) and the dream-digest commit (artifacts/dream-digest/…). Even
 * if a path-escape slipped past the apply-side allowlist (checkPathAllowed), the
 * writer refuses to commit any change outside the dreamer's write surface. Whole-
 * batch fail-closed: one bad path rejects the entire batch (never partially commit).
 *
 * ⚠️ artifacts/ MUST be allowed — the dream DIGEST commits artifacts/dream-digest/
 * <cycle>.{json,md} through THIS commitBatch (digest-commit.ts); dropping it would
 * silently break every digest commit.
 */
const WRITER_ALLOWED_PREFIX = /^(memory|inbox|quarantine|artifacts)\//;
const isAllowedWritePath = (path: string, allowedPrefix: RegExp): boolean =>
  allowedPrefix.test(path) && !path.split("/").includes("..");

const unquote = (s: string): string => s.trim().replace(/^["']|["']$/g, "");

/**
 * Parse `---\nyaml\n---` frontmatter into a flat Record (good enough for
 * origin/source/superseded_by scans). Handles scalars, INLINE arrays
 * (`source: [a, b]`), AND BLOCK-style arrays —
 *   source:
 *     - conv_1
 *     - conv_2
 * — which is exactly the shape apply.ts's `frontmatter()` emits (F7 idempotency
 * relies on `source:` round-tripping back to an array, so block style is NOT
 * optional). A bare `key:` followed by indented `- item` bullets collects into
 * an array; a `key:` with no following bullets stays the empty string.
 */
export function parseFrontmatter(content: string): Record<string, unknown> {
  const m = /^---\n([\s\S]*?)\n---/.exec(content);
  if (!m) return {};
  const fm: Record<string, unknown> = {};
  const lines = m[1].split("\n");
  let pendingKey: string | null = null; // the most recent bare `key:` awaiting block-array items
  for (const line of lines) {
    // A block-array item (`  - value`) belonging to the most recent key.
    const item = /^\s+-\s+(.*)$/.exec(line);
    if (item && pendingKey !== null) {
      if (!Array.isArray(fm[pendingKey])) fm[pendingKey] = [];
      (fm[pendingKey] as string[]).push(unquote(item[1]));
      continue;
    }
    const kv = /^([A-Za-z0-9_-]+):\s*(.*)$/.exec(line.trim());
    if (!kv) { pendingKey = null; continue; }
    const [, k, raw] = kv;
    if (raw.startsWith("[")) {
      fm[k] = raw.slice(1, -1).split(",").map(unquote).filter(Boolean);
      pendingKey = null;
    } else if (raw === "") {
      // Bare `key:` — may be a block-array header. Stay the empty string until a
      // following `- item` upgrades it to an array (apply.ts emits both shapes).
      fm[k] = "";
      pendingKey = k;
    } else {
      fm[k] = unquote(raw);
      pendingKey = null;
    }
  }
  return fm;
}

export function makeGitDataWriter(args: GitDataWriterArgs): BrainRepoWriter {
  const doFetch = args.fetchImpl ?? fetch;
  const repoPath = args.repository;
  const allowedPathPrefix = args.allowedPathPrefix ?? WRITER_ALLOWED_PREFIX;
  // B1 defense-in-depth requires the prefix to anchor at the START of the path —
  // RegExp#test has no implicit start anchor, so an unanchored custom prefix (e.g.
  // /reports\//) would match ANY path containing that substring (e.g. "not-reports/x"),
  // silently widening the dreamer-critical write-path allowlist. Fail fast at
  // construction rather than let a steered write slip past at commit-time.
  if (args.allowedPathPrefix && !args.allowedPathPrefix.source.startsWith("^")) {
    throw new Error(
      `makeGitDataWriter: allowedPathPrefix must be anchored with "^" (got /${args.allowedPathPrefix.source}/) — an unanchored regex would widen the B1 write-path allowlist`,
    );
  }

  // Each public operation mints ONE installation token and threads it through
  // every git-data call in that operation (§2.4): the whole blobs->tree->commit
  // ->update-ref sequence shares a single Bearer, so a multi-step commitBatch
  // mints exactly once.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  async function gh<T>(
    token: string, method: string, path: string, body?: unknown,
  ): Promise<{ status: number; ok: boolean; json: T }> {
    const res = await doFetch(`${API}/repos/${repoPath}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/vnd.github+json",
        "X-GitHub-Api-Version": "2022-11-28",
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const text = await res.text();
    return { status: res.status, ok: res.ok, json: (text ? JSON.parse(text) : {}) as T };
  }

  async function readFileWith(token: string, path: string): Promise<string | null> {
    // Encode each path segment, preserving "/" separators — states the intent
    // directly (vs. encode-everything-then-un-encode-slashes).
    const encodedPath = path.split("/").map(encodeURIComponent).join("/");
    const r = await gh<{ content?: string; encoding?: string }>(
      token, "GET", `/contents/${encodedPath}?ref=${args.branch}`,
    );
    if (r.status === 404) return null;
    if (!r.ok || !r.json.content) return null;
    return Buffer.from(r.json.content, "base64").toString("utf8");
  }

  return {
    async fetchTip(): Promise<string> {
      const token = await args.mintToken();
      const r = await gh<{ object: { sha: string } }>(token, "GET", `/git/refs/heads/${args.branch}`);
      if (!r.ok) throw new Error(`fetchTip: HTTP ${String(r.status)}`);
      return r.json.object.sha;
    },

    async readFile(path: string): Promise<string | null> {
      const token = await args.mintToken();
      return readFileWith(token, path);
    },

    async listMemory(): Promise<MemoryFile[]> {
      const token = await args.mintToken();
      const r = await gh<{ tree: { path: string; type: string }[] }>(
        token, "GET", `/git/trees/${args.branch}?recursive=1`,
      );
      if (!r.ok) return [];
      const paths = r.json.tree
        .filter((e) => e.type === "blob" && e.path.startsWith("memory/") && e.path.endsWith(".md"))
        .map((e) => e.path);
      const out: MemoryFile[] = [];
      for (const path of paths) {
        const content = await readFileWith(token, path);
        out.push({ path, frontmatter: content ? parseFrontmatter(content) : {} });
      }
      return out;
    },

    async commitBatch(a: { expectedOldSha: string; message: string; changes: FileChange[] }) {
      try {
        // B1 defense-in-depth: fail-closed write-path allowlist BEFORE any GitHub call —
        // one out-of-surface path rejects the WHOLE batch (never partially commit a steered write).
        const escaping = a.changes.filter((c) => !isAllowedWritePath(c.path, allowedPathPrefix));
        if (escaping.length > 0) {
          return {
            ok: false as const,
            reason: "error" as const,
            error: `write-path not allowed (B1 allowlist): ${escaping.map((c) => c.path).join(", ")}`,
          };
        }
        const token = await args.mintToken();
        // 1. blobs
        const blobs: { path: string; sha: string }[] = [];
        for (const c of a.changes) {
          const b = await gh<{ sha: string }>(token, "POST", "/git/blobs", {
            content: Buffer.from(c.content, "utf8").toString("base64"),
            encoding: "base64",
          });
          if (!b.ok) return { ok: false as const, reason: "error" as const, error: `blob ${c.path}: HTTP ${String(b.status)}` };
          blobs.push({ path: c.path, sha: b.json.sha });
        }
        // 2. tree on top of the fetched tip's tree (base_tree merges untouched files)
        const tree = await gh<{ sha: string }>(token, "POST", "/git/trees", {
          base_tree: a.expectedOldSha,
          tree: blobs.map((b) => ({ path: b.path, mode: "100644", type: "blob", sha: b.sha })),
        });
        if (!tree.ok) return { ok: false as const, reason: "error" as const, error: `tree: HTTP ${String(tree.status)}` };
        // 3. commit whose SINGLE parent is the expected-old-SHA (the CAS — DESIGN §4.4)
        const commit = await gh<{ sha: string }>(token, "POST", "/git/commits", {
          message: a.message,
          tree: tree.json.sha,
          parents: [a.expectedOldSha],
        });
        if (!commit.ok) return { ok: false as const, reason: "error" as const, error: `commit: HTTP ${String(commit.status)}` };
        // 4. update-ref, force:false → GitHub rejects a non-fast-forward (422) if tip moved
        const ref = await gh<{ object: { sha: string }; message?: string }>(
          token, "PATCH", `/git/refs/heads/${args.branch}`, { sha: commit.json.sha, force: false },
        );
        if (ref.status === 422) return { ok: false as const, reason: "non-fast-forward" as const };
        if (!ref.ok) return { ok: false as const, reason: "error" as const, error: `update-ref: HTTP ${String(ref.status)}` };
        return { ok: true as const, newSha: commit.json.sha };
      } catch (e) {
        return { ok: false as const, reason: "error" as const, error: e instanceof Error ? e.message : String(e) };
      }
    },
  };
}
